/** The work for a source: its tasks (tick to finish, quick add) and its goals. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Bot, ChevronDown, ListChecks, Plus, Repeat, Target } from "lucide-react";
import { Button, DateTimePicker, Input, useToast } from "@/components/ui";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Pill, Section, TickButton, dayLabel, dueLabel, firstName } from "@/features/command/ui";
import { errMsg } from "../shared";
import type { SourceGoal, TaskCard } from "../types";

const TONE: Record<string, string> = { late: "text-rose-400", today: "text-amber-300", soon: "text-zinc-300", later: "text-zinc-500", none: "text-zinc-600" };

function repeatLabel(r: TaskCard["recurrence"]): string {
  if (!r?.freq) return "Repeats";
  const every = (r.interval ?? 1) > 1 ? `every ${r.interval} ` : "every ";
  const unit = { daily: "day", weekly: "week", monthly: "month" }[r.freq] ?? r.freq;
  const days = r.days?.length ? ` on ${r.days.map((d) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]).join(", ")}` : "";
  return `Repeats ${every}${unit}${(r.interval ?? 1) > 1 ? "s" : ""}${days}`;
}

export function WorkSection({ tasks, goals, sourceId, onChanged, onTasks }: {
  tasks: TaskCard[];
  goals: SourceGoal[];
  sourceId: string;
  onChanged: () => void;
  onTasks: (fn: (t: TaskCard[]) => TaskCard[]) => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const open = tasks.filter((t) => t.status !== "completed").sort((a, b) => (a.due_at ?? "9999").localeCompare(b.due_at ?? "9999"));
  const done = tasks.filter((t) => t.status === "completed");

  const toggle = async (t: TaskCard) => {
    const next = t.status === "completed" ? "pending" : "completed";
    onTasks((list) => list.map((x) => (x.id === t.id ? { ...x, status: next, completed_at: next === "completed" ? new Date().toISOString() : null } : x)));
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
      // A routine makes its next copy when it's finished.
      if (t.recurrence && next === "completed") onChanged();
    } catch (e) {
      onTasks((list) => list.map((x) => (x.id === t.id ? t : x)));
      toast.error("Couldn't update the task", errMsg(e));
    }
  };

  const add = async () => {
    const v = title.trim();
    if (!v) return;
    setAdding(true);
    try {
      const r = await api.post<{ task: TaskCard }>("/tasks", {
        title: v, source_id: sourceId, area: "growth",
        ...(due ? { due_at: new Date(`${due.slice(0, 10)}T12:00:00`).toISOString() } : {}),
      });
      onTasks((list) => [...list, { ...r.task, assignee_name: r.task.assignee_name ?? null, goal_title: null, contact_name: null }]);
      setTitle(""); setDue("");
      onChanged();
    } catch (e) {
      toast.error("Couldn't add the task", errMsg(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Section
      title="Work"
      icon={ListChecks}
      count={open.length}
      action={<Link to="/work" className="text-xs font-medium text-indigo-300 hover:text-indigo-200">All work</Link>}
      bodyClassName="p-0"
    >
      <form onSubmit={(e) => { e.preventDefault(); void add(); }} className="flex flex-col gap-2 border-b border-zinc-800/70 p-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task for this source" leftIcon={<Plus className="h-4 w-4" />} maxLength={300} />
        </div>
        <div className="flex gap-2">
          <DateTimePicker value={due} onChange={setDue} includeTime={false} placeholder="Due date" className="w-full sm:w-36" />
          <Button type="submit" size="md" loading={adding} disabled={!title.trim()}>Add</Button>
        </div>
      </form>

      {open.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-zinc-500">Nothing open. Add the next step above.</div>
      ) : (
        <ul className="divide-y divide-zinc-800/60">
          {open.map((t) => <TaskRow key={t.id} t={t} onToggle={() => void toggle(t)} />)}
        </ul>
      )}

      {done.length > 0 && (
        <div className="border-t border-zinc-800/70">
          <button type="button" onClick={() => setShowDone((s) => !s)} className="flex w-full items-center gap-1.5 px-4 py-2 text-xs text-zinc-500 hover:text-zinc-300">
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", !showDone && "-rotate-90")} />
            Done in the last 2 weeks ({done.length})
          </button>
          {showDone && <ul className="divide-y divide-zinc-800/60">{done.map((t) => <TaskRow key={t.id} t={t} onToggle={() => void toggle(t)} />)}</ul>}
        </div>
      )}

      <div className="border-t border-zinc-800/70 px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400"><Target className="h-3.5 w-3.5 text-indigo-300" />Goals</h3>
          <Link to="/work/goals" className="text-[11px] font-medium text-indigo-300 hover:text-indigo-200">{goals.length ? "Open goals" : "Set a goal"}</Link>
        </div>
        {goals.length === 0 ? (
          <p className="text-xs text-zinc-500">No goal tied to this source yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {goals.map((g) => {
              const d = g.due_date ? dueLabel(`${g.due_date}T12:00:00`) : null;
              return (
                <li key={g.id} className="flex items-center gap-2 text-sm">
                  <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", g.status === "done" ? "bg-emerald-400" : g.status === "dropped" ? "bg-zinc-600" : "bg-indigo-400")} />
                  <span className={cn("min-w-0 flex-1 truncate", g.status === "active" ? "text-zinc-200" : "text-zinc-500 line-through")}>{g.title}</span>
                  {g.status !== "active" ? (
                    <Pill color={g.status === "done" ? "emerald" : null}>{g.status === "done" ? "Done" : "Dropped"}</Pill>
                  ) : g.due_date ? (
                    <span className={cn("shrink-0 text-[11px] tabular-nums", d?.tone === "late" ? "text-rose-400" : "text-zinc-500")} title={dayLabel(g.due_date, { weekday: "long", month: "long", day: "numeric" })}>
                      by {dayLabel(g.due_date)}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Section>
  );
}

function TaskRow({ t, onToggle }: { t: TaskCard; onToggle: () => void }) {
  const done = t.status === "completed";
  const due = dueLabel(t.due_at);
  return (
    <li className="flex items-start gap-3 px-4 py-2.5">
      <div className="pt-0.5"><TickButton done={done} onToggle={onToggle} /></div>
      <div className="min-w-0 flex-1">
        <div className={cn("text-sm", done ? "text-zinc-500 line-through" : "text-zinc-100")}>{t.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-zinc-500">
          {!done && <span className={TONE[due.tone]}>{due.text}</span>}
          {t.assignee_name && <span>{firstName(t.assignee_name)}</span>}
          {t.recurrence && <span className="flex items-center gap-1" title={repeatLabel(t.recurrence)}><Repeat className="h-3 w-3" />{repeatLabel(t.recurrence).replace("Repeats ", "")}</span>}
          {t.goal_title && <span className="flex items-center gap-1 truncate"><Target className="h-3 w-3" />{t.goal_title}</span>}
        </div>
      </div>
      {t.agent_slug && (
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-indigo-500/12 px-2 py-0.5 text-[10px] font-semibold text-indigo-300 ring-1 ring-inset ring-indigo-500/25" title="An AI employee does this">
          <Bot className="h-3 w-3" /> AI
        </span>
      )}
    </li>
  );
}
